import type { Language } from 'services/i18n/data';
import config from 'config';

/**
 * Provides the Survicate survey script configuration for the given language.
 *
 * - Returns a script element configuration object when `SURVICATE_ENABLED` is `"true"` and
 *   `SURVICATE_WORKSPACE_ID` is set to a non-empty value.
 * - Returns `undefined` when the feature flag is disabled or the workspace ID is missing.
 *   In development (`DEV`), a warning is logged when the workspace ID is absent despite the flag being enabled.
 *
 * @param language - The UI language used to configure the survey language (normalized to lowercase).
 *
 * @remarks
 * The script is identified by the `id` "survicate-survey-snippet".
 *
 * @see {@link https://survey.survicate.com/} for more information about Survicate surveys.
 */

export function survicateSurveyScript(
  language: Language,
): React.JSX.IntrinsicElements['script'] | undefined {
  const isSurvicateEnabled = JSON.parse(config.SURVICATE_ENABLED || 'false');
  const workspaceId = config.SURVICATE_WORKSPACE_ID?.trim();
  if (!isSurvicateEnabled || !workspaceId) {
    return undefined;
  }

  const init = (lang: Lowercase<Language>) => `
      if (window._sva && typeof window._sva.setSurveyLanguage === 'function') {
        window._sva.setSurveyLanguage("${lang}");
      } else {
        (function(opts) {
          opts.forcedLanguage = "${lang}";
        })(window._sva = window._sva || {});

        (function (w, d) {
          var s = d.createElement('script');
          s.src = 'https://survey.survicate.com/workspaces/${workspaceId}/web_surveys.js';
          s.async = true;
          var e = d.getElementsByTagName('script')[0];

          if (!d.querySelector(\`script[src="\${s.src}"]\`)) {
            e.parentNode.insertBefore(s, e);
          }
        })(window, document);
      }
    `;

  return {
    children: init(language.toLowerCase() as Lowercase<Language>),
    id: 'survicate-survey-snippet',
  };
}
